import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import clientsService from "services/clientsService";
import shiftManagementService from "services/shiftManagementService";
import { SAVE_BUTTON_SX } from "shifts/shiftStyles";
import { Client } from "clients/models";
import { ShiftHubOption, ShiftTypeOption, ShiftTypeRow } from "shifts/models";

// Shift Types — the Flow port of the legacy "Yard Type Update" screen. For one client and
// location, assign each master yard shift its shift type. The type drives downstream
// billing and scheduling, so every shift has to have one before the screen will save.

const ShiftTypes = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [hubs, setHubs] = useState<ShiftHubOption[]>([]);
  const [clientId, setClientId] = useState<number | "">("");
  const [hubId, setHubId] = useState<number | "">("");

  const [rows, setRows] = useState<ShiftTypeRow[]>([]);
  const [shiftTypes, setShiftTypes] = useState<ShiftTypeOption[]>([]);
  const [dirtyIds, setDirtyIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    Promise.all([clientsService.getClients(), shiftManagementService.getHubs()])
      .then(([clientList, hubList]) => {
        setClients(clientList.filter((c) => c.isActive !== false));
        setHubs(hubList);
      })
      .catch(() => toast.error("Could not load clients and locations."));
  }, []);

  const load = useCallback(async () => {
    if (hubId === "") {
      toast.warn("Pick a location first.");
      return;
    }

    setIsLoading(true);
    setDirtyIds(new Set());
    try {
      // Client 0 means every client at the location.
      const result = await shiftManagementService.getShiftTypes({
        clientId: clientId === "" ? 0 : Number(clientId),
        hubId: Number(hubId),
      });
      setRows(result.shifts ?? []);
      setShiftTypes(result.shiftTypes ?? []);
      setHasSearched(true);
    } catch {
      toast.error("Could not load shifts.");
    } finally {
      setIsLoading(false);
    }
  }, [clientId, hubId]);

  const setShiftType = (yardShiftId: number, yardShiftTypeId: number) => {
    setRows((current) =>
      current.map((row) =>
        row.yardShiftId === yardShiftId
          ? {
              ...row,
              yardShiftTypeId,
              shiftType: shiftTypes.find((t) => t.yardShiftTypeId === yardShiftTypeId)?.description ?? "",
            }
          : row
      )
    );
    setDirtyIds((current) => new Set(current).add(yardShiftId));
  };

  const handleSave = async () => {
    if (dirtyIds.size === 0) {
      toast.info("Nothing has changed.");
      return;
    }

    const missing = rows.find((row) => dirtyIds.has(row.yardShiftId) && !row.yardShiftTypeId);
    if (missing) {
      toast.error(`"${missing.shiftName}" at ${missing.location} still needs a shift type.`);
      return;
    }

    setIsSaving(true);
    try {
      const result = await shiftManagementService.saveShiftTypes({
        assignments: rows
          .filter((row) => dirtyIds.has(row.yardShiftId))
          .map((row) => ({ yardShiftId: row.yardShiftId, yardShiftTypeId: row.yardShiftTypeId })),
      });

      if (result.success) {
        toast.success(result.message || "Shift types saved.");
        setDirtyIds(new Set());
      } else {
        toast.error(result.message || "Could not save shift types.");
      }
    } catch {
      toast.error("Could not save shift types.");
    } finally {
      setIsSaving(false);
    }
  };

  const columns: GridColDef[] = [
    { field: "location", headerName: "Location", width: 160, sortable: false },
    { field: "clientName", headerName: "Client", width: 150, sortable: false },
    { field: "shiftName", headerName: "Shift Name", flex: 1, minWidth: 220, sortable: false },
    { field: "timeStart", headerName: "Start", width: 90, sortable: false },
    { field: "timeEnd", headerName: "End", width: 90, sortable: false },
    {
      field: "yardShiftTypeId",
      headerName: "Shift Type",
      width: 220,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftTypeRow;
        return (
          <Select
            value={row.yardShiftTypeId || 0}
            onChange={(e) => setShiftType(row.yardShiftId, Number(e.target.value))}
            variant="standard"
            sx={{ width: "100%", fontSize: ".9em" }}
          >
            <MenuItem value={0}>(none)</MenuItem>
            {shiftTypes.map((type) => (
              <MenuItem key={type.yardShiftTypeId} value={type.yardShiftTypeId}>
                {type.description}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
  ];

  return (
    <Box sx={{ px: 1 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Shift Types
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            {/* shrink/notched keep the label out of the way of the "All clients" placeholder */}
            <InputLabel id="shift-type-client-label" shrink>
              Client (optional)
            </InputLabel>
            <Select
              labelId="shift-type-client-label"
              label="Client (optional)"
              value={clientId}
              onChange={(e) => setClientId(e.target.value === "" ? "" : (e.target.value as number))}
              displayEmpty
              notched
            >
              <MenuItem value="">All clients</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="shift-type-hub-label">Location</InputLabel>
            <Select
              labelId="shift-type-hub-label"
              label="Location"
              value={hubId}
              onChange={(e) => setHubId(e.target.value as number)}
            >
              {hubs.map((hub) => (
                <MenuItem key={hub.hubId} value={hub.hubId}>
                  {hub.location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" color="info" onClick={load} disabled={isLoading}>
            Get Shifts
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || dirtyIds.size === 0}
            sx={SAVE_BUTTON_SX}
          >
            {isSaving ? "Saving…" : `Save${dirtyIds.size ? ` (${dirtyIds.size})` : ""}`}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ height: "calc(100vh - 300px)", minHeight: 360 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.yardShiftId}
          loading={isLoading}
          density="compact"
          disableRowSelectionOnClick
          disableColumnMenu
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
          sx={{ fontSize: ".9em" }}
          slots={{
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {hasSearched ? "No yard shifts for this location." : "Pick a location, then Get Shifts."}
              </Stack>
            ),
          }}
        />
      </Paper>
    </Box>
  );
};

export default ShiftTypes;
